/**
 * Booking Calendar Component - Day Timeline View
 * Shows 30-minute slots from 0:00 to 24:00 for current day
 * Displays individual booking positions with names
 */

import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../services/api';
import { format, addDays, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isSameMonth } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import './BookingCalendar.css';

// Gender symbol helper — U+FE0E forces text rendering (prevents emoji on iOS)
const getGenderSymbol = (gender) => {
  if (gender === 'male') return '♂\uFE0E';
  if (gender === 'female') return '♀\uFE0E';
  if (gender === 'other') return '⚥\uFE0E';
  return '';
};

// Generate days for month calendar grid
const generateMonthDays = (month) => {
  const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  return eachDayOfInterval({ start, end }).map(date => ({
    date,
    isCurrentMonth: isSameMonth(date, month)
  }));
};

// Normalize time to "HH:MM" so "22:00" and "22:00:00" match
const toHHMM = (t) => {
  if (!t) return '';
  return String(t).slice(0, 5);
};

// Eine Buchung gilt fuer das Mitglied allein oder mit einer Begleitperson (wie im Backend)
const MAX_PERSONS_PER_BOOKING = 2;

// Personen einer Buchung; Altbestand ohne Wert zaehlt als 1
const personsOf = (booking) => parseInt(booking.Persons, 10) || 1;
const personsLabel = (n) => (n === 1 ? '1 Person' : `${n} Personen`);

// Get current time in Austria timezone
const getAustriaTime = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Vienna' }));
};

const BookingCalendar = () => {
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showGender, setShowGender] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTime, setCurrentTime] = useState(getAustriaTime());
  const [showMonthCalendar, setShowMonthCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const { user } = useAuth();
  const [selectedStudioId, setSelectedStudioId] = useState(null);

  // Initialize selected studio from user's studios
  useEffect(() => {
    if (user?.studios?.length > 0 && !selectedStudioId) {
      setSelectedStudioId(user.studios[0].id);
    } else if (user?.studioId && !selectedStudioId) {
      setSelectedStudioId(user.studioId);
    }
  }, [user, selectedStudioId]);

  // Aktualisiere aktuelle Zeit jede Minute (Austria timezone)
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(getAustriaTime()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Load data whenever selectedDate changes
  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      try {
        setLoading(true);

        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const nextDay = format(addDays(selectedDate, 1), 'yyyy-MM-dd');

        // Load slots for selected date and studio
        const slotsResponse = await bookingAPI.getSlots(dateStr, nextDay, selectedStudioId);
        if (!isCancelled) setSlots(slotsResponse.data || []);

        // Load ALL bookings for this date (to show all names, not just mine)
        const bookingsResponse = await bookingAPI.getAllBookings(dateStr, nextDay, selectedStudioId);
        if (!isCancelled) {
          setBookings(bookingsResponse.data?.bookings || bookingsResponse.data || []);
          setShowGender(bookingsResponse.data?.showGender || false);
        }

        if (!isCancelled) setError('');
      } catch (err) {
        console.error(err);
        if (!isCancelled) setError('Fehler beim Laden der Daten');
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [selectedDate, selectedStudioId]);

  const handleBook = async (slot, persons) => {
    const date = slot.Date?.split('T')[0] || slot.Date;
    const startTime = toHHMM(slot['Start Time']);
    const dateLabel = format(new Date(`${date}T00:00:00`), 'EEEE, d. MMMM', { locale: de });

    if (!window.confirm(`Jetzt am ${dateLabel} um ${startTime} Uhr für ${personsLabel(persons)} buchen?`)) return;

    try {
      setError('');
      setSuccess('');

      const result = await bookingAPI.createBooking(slot.id, persons);

      const firstName =
        typeof user?.name === 'string' && user.name.trim().length > 0
          ? user.name.trim().split(' ')[0]
          : 'Du';

      // Create new booking object to add to state
      const newBooking = {
        id: result.data?.booking?.id,
        'Client ID': [user?.id],
        Date: date,
        'Start Time': startTime, // normalized
        Persons: persons,
        Status: 'Confirmed',
        clientName: firstName
      };

      // Update bookings state immediately without reload
      setBookings((prevBookings) => [...prevBookings, newBooking]);

      setSuccess(`Gebucht für ${personsLabel(persons)}!`);

      // Clear success message after 2 seconds
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('BookingCalendar - Booking error:', err);
      setError(err.response?.data?.error || 'Buchung fehlgeschlagen');
    }
  };

  const handleCancel = async (booking) => {
    const date = booking.Date?.split('T')[0] || booking.Date;
    const startTime = toHHMM(booking['Start Time']);
    const dateLabel = format(new Date(`${date}T00:00:00`), 'EEEE, d. MMMM', { locale: de });

    if (!window.confirm(`Buchung am ${dateLabel} um ${startTime} Uhr für ${personsLabel(personsOf(booking))} stornieren?`)) return;

    try {
      setError('');
      setSuccess('');
      await bookingAPI.cancelBooking(booking.id);
      setBookings((prevBookings) => prevBookings.filter((b) => b.id !== booking.id));
      setSuccess('Buchung storniert');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Stornierung fehlgeschlagen');
    }
  };

  const changeDate = (days) => {
    setSelectedDate((prev) => addDays(prev, days));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Get bookings for a specific slot (match by date and time)
  const getSlotBookings = (slot) => {
    if (!slot) return [];
    return bookings.filter((booking) => {
      // Normalize date format - ISO -> date-only
      const bookingDate = booking.Date?.split('T')[0] || booking.Date;
      const slotDate = slot.Date?.split('T')[0] || slot.Date;

      const bookingTime = toHHMM(booking['Start Time']);
      const slotTime = toHHMM(slot['Start Time']);

      const status = String(booking.Status || '').toLowerCase();

      return bookingDate === slotDate && bookingTime === slotTime && status === 'confirmed';
    });
  };

  // Freie Plaetze: Kapazitaet minus gebuchte Personen
  const getFreePlaces = (slot) => {
    if (!slot) return 0;
    const capacity = slot.Capacity || 3; // Use capacity from slot (set in backend from settings)
    const taken = getSlotBookings(slot).reduce((sum, b) => sum + personsOf(b), 0);
    return Math.max(capacity - taken, 0);
  };

  // Check if I have booked this slot
  const hasMyBooking = (slot) => {
    if (!slot) return false;
    const slotBookings = getSlotBookings(slot);
    return slotBookings.some((booking) => {
      const clientId = booking['Client ID']?.[0];
      return clientId === user?.id;
    });
  };

  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  // Parse slot datetime
  const getSlotDateTime = (slot) => {
    const slotDate = slot.Date?.split('T')[0] || slot.Date;
    const slotTime = toHHMM(slot['Start Time']);
    return new Date(`${slotDate}T${slotTime}:00`);
  };

  // Prüfe ob Slot älter als 3 Stunden ist (wird ausgeblendet)
  const shouldHideSlot = (slot) => {
    const slotDateTime = getSlotDateTime(slot);
    const threeHoursAgo = new Date(currentTime.getTime() - 3 * 60 * 60 * 1000);
    return slotDateTime < threeHoursAgo;
  };

  // Prüfe ob Slot in der Vergangenheit liegt (grau, nicht buchbar)
  const isSlotInPast = (slot) => {
    const slotDateTime = getSlotDateTime(slot);
    return slotDateTime < currentTime;
  };

  // Prüfe ob es der aktuelle Slot ist (jetzt gerade)
  const isCurrentSlot = (slot) => {
    if (!isToday) return false;
    const slotStart = getSlotDateTime(slot);
    const slotEnd = new Date(slotStart.getTime() + (slot.Duration || 60) * 60000);
    return currentTime >= slotStart && currentTime < slotEnd;
  };

  if (loading) {
    return <div className="loading">Lade Trainingsplan...</div>;
  }

  return (
    <div className="booking-calendar franz-theme">
      {/* Page Header with Logo and Description */}
      <div className="page-header">
        <img
          src="https://cdn.prod.website-files.com/6934931d2112f48107f737f0/6934a5c956d45fd81bd0737d_FRANZ_LOGO_CLEAN_RZ.svg"
          alt="Der Starke Franz"
          className="page-logo"
        />
        <div className="page-description">
          <h1>Trainingskalender{user?.studios?.length === 1 ? ` – ${user.studios[0].name}` : user?.studioName ? ` – ${user.studioName}` : ''}</h1>
          {user?.studios?.length > 1 && (
            <div className="studio-switcher">
              {user.studios.map(s => (
                <button
                  key={s.id}
                  className={`studio-tab ${selectedStudioId === s.id ? 'active' : ''}`}
                  onClick={() => setSelectedStudioId(s.id)}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
          <p>Wähle einen Tag und tippe bei der gewünschten Uhrzeit auf <strong>„1 Person“</strong> oder <strong>„2 Personen“</strong>. Vor dem Buchen fragen wir noch einmal nach.</p>
          <p><strong>1 Person:</strong> Du trainierst allein, andere Mitglieder können den freien Platz noch buchen. <strong>2 Personen:</strong> Du belegst zwei Plätze – für dich und eine Begleitung oder wenn du das Studio für dich allein haben möchtest.</p>
          <p>Stornieren kannst du direkt hier bei deiner Buchung oder unter „Meine Buchungen“.</p>
          <p className="notice">Aus Rücksicht auf die anderen Mitglieder bitten wir dich, deine Einheit rechtzeitig zu stornieren, wenn du nicht trainieren kannst. No-shows werden mit einer Spende ins Kaffee-Schweindi geahndet.</p>
          {(user?.maxTrainingMinutesPerDay > 0 || user?.maxPeakTrainingMinutesPerDay > 0) && (
            <div className="training-limits-info">
              {user.maxTrainingMinutesPerDay > 0 && (
                <span className="limit-badge">Max. Trainingszeit pro Tag: {user.maxTrainingMinutesPerDay} Minuten</span>
              )}
              {user.maxPeakTrainingMinutesPerDay > 0 && (
                <span className="limit-badge peak">Max. Trainingszeit in Stoßzeiten: {user.maxPeakTrainingMinutesPerDay} Minuten</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Month Calendar Picker */}
      {showMonthCalendar && (
        <div className="month-calendar">
          <div className="month-header">
            <button onClick={() => setCalendarMonth(prev => addMonths(prev, -1))} className="month-nav-btn">←</button>
            <span className="month-title">{format(calendarMonth, 'MMMM yyyy', { locale: de })}</span>
            <button onClick={() => setCalendarMonth(prev => addMonths(prev, 1))} className="month-nav-btn">→</button>
          </div>
          <div className="month-grid">
            {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(d => (
              <div key={d} className="weekday-label">{d}</div>
            ))}
            {generateMonthDays(calendarMonth).map((day, i) => (
              <button
                key={i}
                className={`day-cell ${day.isCurrentMonth ? '' : 'other-month'} ${isSameDay(day.date, selectedDate) ? 'selected' : ''} ${isSameDay(day.date, new Date()) ? 'today' : ''}`}
                onClick={() => { setSelectedDate(day.date); setShowMonthCalendar(false); }}
              >
                {day.date.getDate()}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Date Navigation */}
      <div className="date-navigation">
        <button onClick={() => changeDate(-1)} className="nav-button">
          ← Vorheriger Tag
        </button>

        <div className="current-date">
          <button onClick={() => setShowMonthCalendar(!showMonthCalendar)} className="calendar-toggle" title="Kalender öffnen">
            📅
          </button>
          <h2>{format(selectedDate, 'EEEE, d. MMMM yyyy', { locale: de })}</h2>
          {!isToday && (
            <button onClick={goToToday} className="today-button">
              Heute
            </button>
          )}
        </div>

        <button onClick={() => changeDate(1)} className="nav-button">
          Nächster Tag →
        </button>
      </div>

      {/* Toast Messages - Fixed position so always visible */}
      {(error || success) && (
        <div className={`toast-message ${error ? 'error' : 'success'}`}>
          {error || success}
        </div>
      )}

      {/* Timeline */}
      <div className="timeline">
        {slots.filter((slot) => !shouldHideSlot(slot)).map((slot) => {
          const slotBookings = getSlotBookings(slot);
          const freePlaces = getFreePlaces(slot);
          const isFull = freePlaces === 0;
          const hasBooked = hasMyBooking(slot);
          const isPast = isSlotInPast(slot);
          const isCurrent = isCurrentSlot(slot);
          const canBook = !isFull && !isPast && !hasBooked;
          const personOptions = Array.from(
            { length: Math.min(freePlaces, MAX_PERSONS_PER_BOOKING) },
            (_, i) => i + 1
          );

          return (
            <div
              key={slot.id}
              className={`time-slot exists ${isFull ? 'full' : ''} ${hasBooked ? 'my-booking' : ''} ${
                isPast ? 'past' : ''
              } ${isCurrent ? 'current-slot' : ''} ${slot.isPeak ? 'peak' : ''}`}
            >
              <div className="time-label">{toHHMM(slot['Start Time'])}</div>

              <div className="slot-positions">
                {slotBookings.map((booking, idx) => {
                  const isMe = booking['Client ID']?.[0] === user?.id;
                  return (
                    <div key={booking.id || idx} className={`position booked ${isMe ? 'is-me' : ''}`}>
                      <div className="position-name">
                        {isMe ? 'Du' : booking.clientName || 'Gebucht'}
                        <span className="position-persons">{personsLabel(personsOf(booking))}</span>
                      </div>
                      {showGender && !isMe && booking.clientGender && (
                        <span className={`gender-badge ${booking.clientGender}`}>
                          {getGenderSymbol(booking.clientGender)}
                        </span>
                      )}
                      {isMe && !isPast && booking.id && (
                        <button type="button" className="position-cancel" onClick={() => handleCancel(booking)}>
                          ✕ Stornieren
                        </button>
                      )}
                    </div>
                  );
                })}

                {canBook && (
                  <div className="position free bookable">
                    <div className="position-name">Frei – buchen für:</div>
                    <div className="book-options">
                      {personOptions.map((n) => (
                        <button key={n} type="button" className="book-option" onClick={() => handleBook(slot, n)}>
                          {personsLabel(n)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!canBook && !isFull && (
                  <div className="position free">
                    <div className="position-name">
                      {freePlaces === 1 ? '1 Platz frei' : `${freePlaces} Plätze frei`}
                    </div>
                  </div>
                )}
              </div>

              <div className="slot-status">
                {isFull && <div className="full-badge">Ausgebucht</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BookingCalendar;